import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { TextField, DefaultButton, PrimaryButton, Checkbox, DatePicker, DefaultPalette } from '@fluentui/react';
import { ICommandBarItemProps } from '@fluentui/react/lib/CommandBar';
import { useTranslation } from 'react-i18next';
import { DateTime } from 'luxon';
import TimeTaxSupportSystemClient from '../../system';
import MessageCourier from '../../lib/MessageCourier';
import { routes } from '../../common/constants';
import { ProyectoGlosaSavePayload } from '../../system/proyectos_glosa/ProyectosGlosaSystem';
import { defaultCommandBarButtonStyles, titleCommandBarButtonStyles } from '../../styles/button-styles';
import ViewWrapperWithCommandBar from '../Layout/ViewWrapperWithCommandBar';
import { validateProyectosGlosa, hasErrors, ProyectosGlosaFormValues, ProyectosGlosaErrors } from './ProyectosGlosaValidation';
import { getLocalizedStrings } from '../../lib/LocalizedStringsForDatePicker';

const formatDateStart = (date: Date | null | undefined): string => {
  if (!date) return '';
  return DateTime.fromJSDate(date).toFormat('yyyy-LL-dd');
};

const formatDateEnd = (date: Date | null | undefined): string => {
  if (!date) return '';
  return DateTime.fromJSDate(date).toFormat('yyyy-LL-dd');
};

interface ProyectosGlosaFormProps {
  system: TimeTaxSupportSystemClient;
  courier: MessageCourier;
  changeBlockUI: (v: boolean) => void;
}

const ProyectosGlosaForm: React.FC<ProyectosGlosaFormProps> = (props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [formData, setFormData] = useState<ProyectosGlosaFormValues>({
    dicprog_nombre_proyecto: '',
    dicprog_fecha_validez_inicio: undefined,
    dicprog_fecha_validez_fin: undefined,
    dicprog_activo: true,
  });

  const [errors, setErrors] = useState<ProyectosGlosaErrors>({});

  const handleSave = async () => {
    const validationErrors = validateProyectosGlosa(formData);
    setErrors(validationErrors);

    if (hasErrors(validationErrors)) {
      return;
    }

    props.changeBlockUI(true);
    try {
      const payload: ProyectoGlosaSavePayload = {
        dicprog_nombre_proyecto: formData.dicprog_nombre_proyecto,
        dicprog_fecha_validez_inicio: formatDateStart(formData.dicprog_fecha_validez_inicio),
        dicprog_fecha_validez_fin: formatDateEnd(formData.dicprog_fecha_validez_fin),
        dicprog_activo: formData.dicprog_activo,
      };

      await props.system.getProyectosGlosaSystem().save(payload);
      props.courier.messageSuccess(t('Registro creado exitosamente'));
      navigate(routes.proyectos_glosa);
    } catch (error) {
      props.courier.messageError(t('Error al guardar el registro'));
    } finally {
      props.changeBlockUI(false);
    }
  };

  const updateField = (field: keyof ProyectosGlosaFormValues, value: string | boolean | Date | undefined) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const itemsMenu = React.useMemo(
    (): ICommandBarItemProps[] => [
      {
        key: 'title',
        text: t('Nuevo Proyecto Glosa'),
        iconProps: {
          iconName: 'Dictionary',
          color: DefaultPalette.themeLighterAlt,
          className: 'text-btn',
        },
        buttonStyles: titleCommandBarButtonStyles,
        className: 'text-btn',
      },
      {
        key: 'back',
        text: t('Volver'),
        iconProps: {
          iconName: 'Back',
          color: DefaultPalette.themeLighterAlt,
          className: 'text-btn',
        },
        buttonStyles: titleCommandBarButtonStyles,
        className: 'text-btn',
        onClick: () => navigate(routes.proyectos_glosa),
      },
    ],
    [],
  );

  return (
    <ViewWrapperWithCommandBar
      withBorderBottom
      title=""
      items={itemsMenu}
      overflowButtonStyles={defaultCommandBarButtonStyles}
      variant="highligth"
      titleBody={t('Nuevo Proyecto Comercial')}
    >
      <div className="padding-1">
        <div className="ms-Grid bg-white padding-1" dir="ltr">
          <div style={{ maxWidth: 800 }}>
            <div className="ms-Grid-row">
              <div className="ms-Grid-col ms-lg6 ms-sm12">
                <TextField
                  label={t('Nombre del proyecto')}
                  value={formData.dicprog_nombre_proyecto}
                  onChange={(e, val) => updateField('dicprog_nombre_proyecto', val || '')}
                  errorMessage={errors.dicprog_nombre_proyecto}
                />
              </div>
            </div>
            <div className="ms-Grid-row mt-2">
              <div className="ms-Grid-col ms-lg3 ms-sm6">
                <DatePicker
                  label={t('Inicio de vigencia')}
                  value={formData.dicprog_fecha_validez_inicio}
                  onSelectDate={(date) => updateField('dicprog_fecha_validez_inicio', date || undefined)}
                  formatDate={(date) => (date ? DateTime.fromJSDate(date).toFormat('dd/MM/yyyy') : '')}
                  strings={getLocalizedStrings()}
                />
                {errors.dicprog_fecha_validez_inicio && (
                  <div style={{ color: '#a4262c', fontSize: 12, marginTop: 5 }}>{errors.dicprog_fecha_validez_inicio}</div>
                )}
              </div>
              <div className="ms-Grid-col ms-lg3 ms-sm6">
                <DatePicker
                  label={t('Fin de vigencia')}
                  value={formData.dicprog_fecha_validez_fin}
                  onSelectDate={(date) => updateField('dicprog_fecha_validez_fin', date || undefined)}
                  formatDate={(date) => (date ? DateTime.fromJSDate(date).toFormat('dd/MM/yyyy') : '')}
                  strings={getLocalizedStrings()}
                />
                {errors.dicprog_fecha_validez_fin && (
                  <div style={{ color: '#a4262c', fontSize: 12, marginTop: 5 }}>{errors.dicprog_fecha_validez_fin}</div>
                )}
              </div>
            </div>
            <div className="ms-Grid-row mt-2">
              <div className="ms-Grid-col ms-lg6 ms-sm12">
                <Checkbox label={t('Activo')} checked={formData.dicprog_activo} onChange={(e, checked) => updateField('dicprog_activo', checked)} />
              </div>
            </div>
            <div className="ms-Grid-row mt-4">
              <div className="ms-Grid-col ms-lg6 ms-sm12" style={{ display: 'flex', gap: 8 }}>
                <PrimaryButton text={t('Guardar')} onClick={handleSave} />
                <DefaultButton text={t('Cancelar')} onClick={() => navigate(routes.proyectos_glosa)} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </ViewWrapperWithCommandBar>
  );
};

export default ProyectosGlosaForm;
